import { Box } from '@mui/material';
import { keyframes, styled } from '@mui/material/styles';
import { HEADER } from 'src/config-global';
import { authFormEntrance } from 'src/layouts/auth-form-animation';

const artworkEntrance = keyframes`
  from { opacity: 0; transform: translateY(2%); }
  to { opacity: 1; transform: translateY(0); }
`;

const headingEntrance = keyframes`
  from { opacity: 0; transform: translateY(35%); filter: blur(0.1em); }
  to { opacity: 1; transform: translateY(0); filter: blur(0); }
`;

export const FlowRoot = styled('div')(({ theme }) => ({
  minHeight: '100svh',
  backgroundColor: theme.palette.background.default,
  paddingTop: HEADER.H_MOBILE,
  [theme.breakpoints.up('lg')]: { paddingTop: HEADER.H_MAIN_DESKTOP },
}));

export const FlowMain = styled('main')(({ theme }) => ({
  display: 'grid',
  backgroundColor: theme.palette.background.neutral,
  gridTemplateColumns: 'minmax(0, 1fr)',
  gridTemplateRows: '1fr',
  minHeight: `calc(100svh - ${HEADER.H_MOBILE}px)`,
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 0.9fr)',
    minHeight: `calc(100svh - ${HEADER.H_MAIN_DESKTOP}px)`,
  },
}));

export const FlowWelcome = styled('section')(({ theme }) => ({
  position: 'sticky',
  top: `calc(${HEADER.H_MAIN_DESKTOP}px + ${theme.spacing(3)})`,
  alignSelf: 'start',
  isolation: 'isolate',
  overflow: 'hidden',
  minWidth: 0,
  height: `calc(100svh - ${HEADER.H_MAIN_DESKTOP}px - ${theme.spacing(6)})`,
  minHeight: theme.spacing(56),
  margin: theme.spacing(3, 0, 3, 3),
  borderRadius: theme.spacing(4),
  backgroundColor: theme.palette.background.neutral,
  color: theme.palette.text.primary,
}));

export const FlowHeading = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  padding: theme.spacing(5, 5, 0),
  [theme.breakpoints.up('xl')]: { padding: theme.spacing(6, 6, 0) },
}));

export const FlowHeadingLine = styled('span')({
  display: 'inline-block',
  animation: `${headingEntrance} 600ms cubic-bezier(0.16, 1, 0.3, 1) 350ms both`,
  animationPlayState: 'var(--welcome-animation-play-state, paused)',
  '&:last-child': { animationDelay: '490ms' },
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

export const FlowArtwork = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '106%',
  maxWidth: theme.spacing(110),
  height: 'auto',
  aspectRatio: '3 / 2',
  maxHeight: `calc(100% - ${theme.spacing(25)})`,
  left: '-3%',
  right: '-3%',
  margin: '0 auto',
  bottom: 0,
  animation: `${artworkEntrance} 650ms ease-out both`,
  animationPlayState: 'var(--welcome-animation-play-state, paused)',
  pointerEvents: 'none',
  maskImage:
    'linear-gradient(to bottom, transparent, black 12%, black 92%, transparent), linear-gradient(to right, transparent, black 4%, black 96%, transparent)',
  maskComposite: 'intersect',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
}));

export const FlowColumn = styled('section')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  minWidth: 0,
  padding: theme.spacing(3, 2),
  [theme.breakpoints.up('md')]: { padding: theme.spacing(4) },
  [theme.breakpoints.up('lg')]: { justifyContent: 'center' },
  [theme.breakpoints.up('xl')]: { padding: theme.spacing(5, 6) },
}));

export const FlowCard = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: theme.spacing(69),
  padding: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
  boxShadow: theme.customShadows.z8,
  overflowWrap: 'anywhere',
  animation: `${authFormEntrance} 560ms cubic-bezier(0.16, 1, 0.3, 1) 180ms both`,
  animationPlayState: 'var(--welcome-animation-play-state, paused)',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  [theme.breakpoints.up('md')]: { padding: theme.spacing(4) },
  [theme.breakpoints.up('lg')]: { borderRadius: theme.spacing(4) },
}));
